import isEmpty from "lodash/isEmpty";
import React, { type FC, type ReactNode } from "react";
import { Table } from "semantic-ui-react";
import { DragAndDropArea, SearchInput, TextTruncate } from "../../../../../../components";
import MinusButton from "../../../../../../components/buttons/iconButtons/minusButton/MinusButton";
import Tooltip from "../../../../../../components/common/tooltip/Tooltip";
import { UsersFilterForm } from "../../../../../../components/filterForms";
import { ListViewBase } from "../../../../../../components/listView";
import NoResults from "../../../../../../components/noResults/NoResults";
import NoSearchResults from "../../../../../../components/noSearchResults";
import UserInfoCell from "../../../../../../components/userInfoCell/UserInfoCell";
import { SortingDirection, ViewType } from "../../../../../../enums";
import dateTimeUtils from "../../../../../../utils/dateTimeUtils";
import { ItemsView } from "../../../../../../views";
import "./groupUsersList.scss";
import { columnOptionsAutoEnroll, columnOptionsManualEnroll, membersListViewRtnEvents } from "./helpers";
import { type UserInfoBase } from "../../../../types";
import type { Filters } from "../../../../../../utils/queryUtils";
import { type GenericFiltersMap } from "utils/filterUtils";
import { type Dispatch } from "redux";
import { useDragAndDropSection } from "../../../../../../components/dragAndDrop/DragAndDropSections/useDragAndDropSection";
import validationRules, { type ValidationValue } from "../../../../../../utils/validationRules";
import fileUtils from "../../../../../../utils/fileUtils";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

export interface Props {
  isAutoEnrollment?: boolean;
  isDragAndDrop?: boolean;
  isGroupCreation?: boolean;
  isReadOnly?: boolean;
  addMembersBtn?: React.ReactNode;
  renderHeaderSection: boolean;
  filter?: Record<string, unknown>;
  users: UserInfoBase[];
  usersCount: number;
  loadPage: (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    appliedFilter: Filters,
  ) => void;
  selectedItemIds?: number[];
  updateSelectedItems?: (payload: number[]) => void;
  isLoading?: boolean;
  appliedUserFilter?: Record<string, unknown>;
  filterOptions?: Filters;
  setUserFilter?: (payload: GenericFiltersMap<string | number>) => void;
  resetUserFilter?: () => void;
  getFilterOptions?: () => (dispatch: Dispatch) => Promise<void>;
  search?: string;
  onSearchChanged?: (value: string) => void;
  hideUserRemoveColumn?: boolean;
  setReloadListItems?: (reloadListItemsFunc: (enableSorting: boolean) => void) => void;
  deepLink?: boolean;
  customNoResultContent?: () => ReactNode;
  onRemoveMemberClick?: (userId: number) => void;
  disableSearch?: boolean;
  onFileDropped?: (file: File) => void;
}

const GroupUsersList: FC<Props> = (props) => {
  const dndProps = useDragAndDropSection({
    onFileDropped: props.onFileDropped!,
    isValidFileType: (value: ValidationValue) => validationRules.csvFileType(value),
    isValidFileExtension: (file: File) => fileUtils.isCsvExtension(file),
  });

  const buildTableBodyAutoEnroll = (user: UserInfoBase) => {
    const columnOptionsAutoEnroll = columnOptionsManualEnroll;
    return (
      <>
        <Table.Cell width={columnOptionsAutoEnroll[0].width}>
          <UserInfoCell deepLink={props.deepLink} user={user} />
        </Table.Cell>
        <Table.Cell width={columnOptionsAutoEnroll[1].width}>
          <Tooltip target={<TextTruncate>{user.department}</TextTruncate>} content={user.department} />
        </Table.Cell>
        <Table.Cell width={columnOptionsAutoEnroll[2].width}>
          <Tooltip
            target={<TextTruncate>{user.title ?? user.jobTitle}</TextTruncate>}
            content={user.title ?? user.jobTitle}
          />
        </Table.Cell>
        <Table.Cell width={columnOptionsAutoEnroll[3].width}>{dateTimeUtils.formatDate(user.createDate)}</Table.Cell>
      </>
    );
  };

  const buildTableBodyManualEnroll = (user: UserInfoBase, hideUserRemoveColumn: boolean) => {
    return (
      <>
        <Table.Cell width={columnOptionsManualEnroll[0].width}>
          <UserInfoCell deepLink={props.deepLink} user={user} />
        </Table.Cell>
        <Table.Cell width={columnOptionsManualEnroll[1].width}>
          <Tooltip target={<TextTruncate>{user.department}</TextTruncate>} content={user.department} />
        </Table.Cell>
        <Table.Cell width={columnOptionsManualEnroll[2].width}>
          <Tooltip target={<TextTruncate>{user.title}</TextTruncate>} content={user.title} />
        </Table.Cell>
        <Table.Cell width={columnOptionsManualEnroll[3].width}>
          {user.joinedDate != null
            ? dateTimeUtils.formatDate(user.joinedDate)
            : dateTimeUtils.formatDate(user.createDate)}
        </Table.Cell>
        {!hideUserRemoveColumn && renderRemoveButton(user.id!)}
      </>
    );
  };

  const renderRemoveButton = (userId: number) => {
    const { isReadOnly, onRemoveMemberClick, selectedItemIds } = props;

    return (
      <Table.Cell className="align-right-groups" width={columnOptionsManualEnroll[4].width}>
        <Tooltip
          position="top center"
          hideOnScroll
          showAlways
          disabled={selectedItemIds?.includes(userId) || isReadOnly}
          target={
            <MinusButton
              onClick={() => onRemoveMemberClick?.(userId)}
              isDisabled={selectedItemIds?.includes(userId) || isReadOnly}
            />
          }
          content="Remove"
        />
      </Table.Cell>
    );
  };

  const noResultsContent = () => {
    if (!isEmpty(props.search)) {
      return <NoSearchResults />;
    }

    if (props.isAutoEnrollment) {
      return (
        <NoResults
          title="No Members"
          description="Select some enrollment rules to preview members here"
          iconClassName="fal fa-user"
        />
      );
    }

    if (props.isGroupCreation) {
      return <NoSearchResults />;
    }

    if (isEmpty(props.appliedUserFilter)) {
      return (
        <NoResults
          title={props.isDragAndDrop ? "Drag and Drop Here to Import" : "You don't have any members"}
          description={
            props.isDragAndDrop
              ? "Looks like you don’t have any users yet.  Drag and drop file (.csv) here to import."
              : "Looks like you don't have any members yet. Add some using the button below."
          }
          iconClassName={props.isDragAndDrop ? undefined : "fal fa-user"}
          fontAwesomeIcon={props.isDragAndDrop ? "faSquareDashedCirclePlus" : undefined}
        >
          {props.addMembersBtn}
        </NoResults>
      );
    }
    return <NoResults title="No Members found" iconClassName="fal fa-user" />;
  };

  // @ts-ignore
  const getFilterForm = () => <UsersFilterForm />;

  const renderWithoutHeader = () => {
    return (
      <ListViewBase
        className="user-selection-list"
        viewType={ViewType.LIST}
        columnOptions={columnOptionsAutoEnroll}
        loadPage={props.loadPage}
        itemsAmount={props.usersCount}
        isLoading={props.isLoading}
        items={props.users}
        withSelection={false}
        withFooter={false}
        filter={props.filter}
        noResultsContent={noResultsContent()}
        buildTableBody={buildTableBodyAutoEnroll}
        sortingColumnName="Added"
        sortingDirection={SortingDirection.Descending}
        selectedItemIds={props.selectedItemIds}
        updateSelectedItems={props.updateSelectedItems}
      />
    );
  };

  const renderWithHeader = () => {
    const renderNoResultsContent = props.customNoResultContent || noResultsContent;

    return (
      <ItemsView
        className="user-selection-list"
        viewType={ViewType.LIST}
        columnOptions={columnOptionsManualEnroll}
        getData={props.loadPage}
        itemsInlineCount={props.usersCount}
        isLoading={props.isLoading}
        items={props.users}
        buildTableBody={(user: UserInfoBase) => buildTableBodyManualEnroll(user, !!props.hideUserRemoveColumn)}
        onSelectedListItemsChanged={props.updateSelectedItems}
        renderFilterForm={getFilterForm}
        filterOptionsLoading={props.filterOptions?.isLoading}
        appliedFilter={props.appliedUserFilter}
        filterOptions={props.filterOptions}
        getFilterOptions={props.getFilterOptions}
        applyFilter={props.setUserFilter}
        resetFilter={props.resetUserFilter}
        sortingColumnName="Added"
        sortingDirection={SortingDirection.Descending}
        noResultsContent={renderNoResultsContent()}
        selectedIds={props.selectedItemIds}
        isSelectDisabled={() => props.isReadOnly}
        renderSearch={() => (
          <SearchInput
            placeholder="Search for Users..."
            defaultValue={props.search}
            onChange={props.onSearchChanged}
            disabled={props.disableSearch}
          />
        )}
        setReloadListItems={props.setReloadListItems}
        listViewRtnEvents={membersListViewRtnEvents}
        dragAndDrop={
          props.isDragAndDrop ? (
            <DndProvider backend={HTML5Backend}>
              <DragAndDropArea {...dndProps} />
            </DndProvider>
          ) : null
        }
      />
    );
  };

  return props.renderHeaderSection ? renderWithHeader() : renderWithoutHeader();
};

export default GroupUsersList;
